/* ============================================================================
 * SYLONI® · Motion Base (Sprint 0)
 *
 * Foundation runtime for all motion effects:
 *   1. Detect device capability (memory, cores, connection, touch)
 *   2. Set HTML class hooks (.syloni-no-motion, .syloni-touch-device)
 *   3. Provide IntersectionObserver for .syloni-fade-up / .syloni-stagger
 *   4. Provide global utilities (debounce, throttle, raf-loop) on window.SYLONI
 *
 * Loaded once via snippets/syloni-motion-init.liquid.
 * Other syloni-*.js files DEPEND on this — load order matters.
 *
 * Last update: 2026-04-27 · Sprint 0
 * ============================================================================ */

(function () {
  'use strict';

  // ───────────────────────────────────────────────────────────────
  // 1 · Device capability detection
  // ───────────────────────────────────────────────────────────────
  const docEl = document.documentElement;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  const isPrimaryTouch = window.matchMedia('(pointer: coarse)').matches;

  // Hardware
  const memGB = navigator.deviceMemory || 4;
  const cores = navigator.hardwareConcurrency || 4;
  const conn = (navigator.connection && navigator.connection.effectiveType) || '4g';
  const saveData = (navigator.connection && navigator.connection.saveData) === true;

  const isLowEnd =
    memGB < 4 ||
    cores < 4 ||
    conn === '2g' ||
    conn === 'slow-2g' ||
    saveData;

  // Apply class hooks on <html>
  if (reducedMotion || isLowEnd) {
    docEl.classList.add('syloni-no-motion');
  }
  if (isPrimaryTouch || hasTouch) {
    docEl.classList.add('syloni-touch-device');
  }

  // ───────────────────────────────────────────────────────────────
  // 2 · Global namespace · window.SYLONI
  // ───────────────────────────────────────────────────────────────
  const SYLONI = {
    // Capability flags (read-only from outside)
    reducedMotion,
    isLowEnd,
    isPrimaryTouch,
    hasTouch,
    memGB,
    cores,
    conn,
    saveData,

    // Build/version stamp
    version: '0.1.0-sprint0',

    // Module registry — sprints 1-4 register their controllers here
    modules: {},

    // Utilities
    debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    throttle(fn, wait) {
      let last = 0;
      let t;
      return function (...args) {
        const now = performance.now();
        const remaining = wait - (now - last);
        if (remaining <= 0) {
          last = now;
          fn.apply(this, args);
        } else {
          clearTimeout(t);
          t = setTimeout(() => {
            last = performance.now();
            fn.apply(this, args);
          }, remaining);
        }
      };
    },

    raf(fn) {
      let running = false;
      let stop = false;
      function loop() {
        if (stop) return;
        fn();
        requestAnimationFrame(loop);
      }
      return {
        start() {
          if (running) return;
          running = true;
          stop = false;
          loop();
        },
        stop() {
          stop = true;
          running = false;
        }
      };
    },

    // Spring physics for particle / tilt motion
    spring(current, target, velocity, stiffness, damping) {
      const force = (target - current) * stiffness;
      velocity = (velocity + force) * damping;
      return { value: current + velocity, velocity };
    },

    // Easing functions
    easing: {
      linear: t => t,
      easeOut: t => 1 - Math.pow(1 - t, 3),
      easeIn: t => t * t * t,
      easeInOut: t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
      bouncy: t => {
        const c4 = (2 * Math.PI) / 3;
        return t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
      }
    },

    // Color helpers
    hexToRgb(hex) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return [r, g, b];
    },

    rgba(hex, a) {
      const [r, g, b] = SYLONI.hexToRgb(hex);
      return `rgba(${r},${g},${b},${a})`;
    }
  };

  window.SYLONI = SYLONI;

  // ───────────────────────────────────────────────────────────────
  // 3 · IntersectionObserver for .syloni-fade-up / .syloni-stagger
  // ───────────────────────────────────────────────────────────────
  if (!reducedMotion && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    function scan() {
      document.querySelectorAll('.syloni-fade-up:not(.is-in), .syloni-stagger:not(.is-in)').forEach((el) => {
        io.observe(el);
      });
    }

    SYLONI.scanReveal = scan; // Expose for dynamic content (Sprint 5+)

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scan);
    } else {
      scan();
    }
  } else {
    // No IO support OR reduced motion: instantly mark all as in
    document.querySelectorAll('.syloni-fade-up, .syloni-stagger').forEach((el) => {
      el.classList.add('is-in');
    });
  }

  // ───────────────────────────────────────────────────────────────
  // 4 · Console boot banner
  // ───────────────────────────────────────────────────────────────
  console.log(
    '%c SYLONI® %c Motion Base v0.1.0 ',
    'background:#0A0A0A;color:#D4AF37;padding:6px 12px;font-family:monospace;letter-spacing:4px;font-weight:700;',
    'background:transparent;color:#5A5A5A;padding:6px 8px;font-family:monospace;'
  );
  console.log(
    '%cdevice · ' +
      `mem=${memGB}GB · cores=${cores} · conn=${conn} · ` +
      `touch=${hasTouch} · reduced=${reducedMotion} · low-end=${isLowEnd}`,
    'color:#5A5A5A;font-family:monospace;font-size:11px;'
  );
})();
